import { useState } from "react";
import { Badge, Code, Group, Select, Stack, Text, TextInput, Title } from "@mantine/core";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { qk } from "@/lib/apiHelpers";
import { DataTable, DataTableColumn } from "@/components/DataTable";
import type { AuditEntry, AuditListResponse } from "@/types/api";

const ENTITY_TYPES = [
  { value: "", label: "Все сущности" },
  { value: "user", label: "Пользователи" },
  { value: "role", label: "Роли" },
  { value: "company", label: "Фирмы" },
  { value: "session", label: "Сессии" },
];

const ACTIONS = [
  { value: "", label: "Все действия" },
  { value: "create", label: "Создание" },
  { value: "update", label: "Изменение" },
  { value: "delete", label: "Удаление" },
  { value: "login", label: "Вход" },
  { value: "logout", label: "Выход" },
  { value: "login_failed", label: "Неудачный вход" },
  { value: "password_change", label: "Смена пароля" },
  { value: "password_reset", label: "Сброс пароля" },
  { value: "permissions_set", label: "Изменение прав" },
];

const ACTION_COLORS: Record<string, string> = {
  create: "green",
  update: "blue",
  delete: "red",
  login: "teal",
  logout: "gray",
  login_failed: "orange",
  password_change: "violet",
  password_reset: "violet",
  permissions_set: "indigo",
};

export default function AuditPage() {
  const [page, setPage] = useState(1);
  const [entityType, setEntityType] = useState("");
  const [action, setAction] = useState("");
  const [entityID, setEntityID] = useState("");

  const filters = { page, page_size: 50, entity_type: entityType, action, entity_id: entityID };
  const { data, isLoading } = useQuery({
    queryKey: qk.audit(filters),
    queryFn: async () => {
      const params = new URLSearchParams();
      params.set("page", String(filters.page));
      params.set("page_size", String(filters.page_size));
      if (filters.entity_type) params.set("entity_type", filters.entity_type);
      if (filters.action) params.set("action", filters.action);
      if (filters.entity_id) params.set("entity_id", filters.entity_id);
      return (await api.get<AuditListResponse>(`/api/v1/audit?${params.toString()}`)).data;
    },
  });

  const columns: DataTableColumn<AuditEntry>[] = [
    {
      key: "when",
      header: "Когда",
      width: 160,
      render: (e) => new Date(e.created_at).toLocaleString("ru-RU"),
    },
    {
      key: "user",
      header: "Кто",
      width: 160,
      render: (e) => e.user_login || <Text c="dimmed" size="sm">—</Text>,
    },
    {
      key: "action",
      header: "Действие",
      width: 140,
      render: (e) => (
        <Badge color={ACTION_COLORS[e.action] ?? "gray"} variant="light">{e.action}</Badge>
      ),
    },
    {
      key: "entity",
      header: "Сущность",
      width: 110,
      render: (e) => <Code>{e.entity_type}</Code>,
    },
    {
      key: "entity_id",
      header: "ID",
      width: 200,
      render: (e) => <Text size="xs" ff="monospace" c="dimmed">{e.entity_id.slice(0, 16)}…</Text>,
    },
    {
      key: "changes",
      header: "Изменения",
      render: (e) =>
        e.changes && Object.keys(e.changes).length > 0 ? (
          <Code block style={{ fontSize: 11, maxWidth: 360 }}>
            {JSON.stringify(e.changes, null, 1)}
          </Code>
        ) : (
          <Text c="dimmed" size="sm">—</Text>
        ),
    },
    {
      key: "ip",
      header: "IP",
      width: 120,
      render: (e) => e.ip || <Text c="dimmed" size="sm">—</Text>,
    },
  ];

  return (
    <Stack gap="lg">
      <Title order={2}>Аудит</Title>
      <Group>
        <Select
          label="Сущность"
          data={ENTITY_TYPES}
          value={entityType}
          onChange={(v) => { setEntityType(v ?? ""); setPage(1); }}
          style={{ width: 180 }}
        />
        <Select
          label="Действие"
          data={ACTIONS}
          value={action}
          onChange={(v) => { setAction(v ?? ""); setPage(1); }}
          style={{ width: 200 }}
        />
        <TextInput
          label="ID сущности"
          value={entityID}
          onChange={(e) => { setEntityID(e.currentTarget.value); setPage(1); }}
          style={{ width: 320 }}
          placeholder="UUID или ID"
        />
      </Group>
      <DataTable
        columns={columns}
        rows={data?.items}
        loading={isLoading}
        total={data?.total ?? 0}
        page={page}
        pageSize={50}
        onPageChange={setPage}
        rowKey={(e) => String(e.id)}
      />
    </Stack>
  );
}
